import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import {
	IPlatformCapabilityField,
	PLATFORM_CAPABILITY_FIELDS,
	findPlatformCapabilities,
	formatCapabilityValue,
	getBindingPlatforms,
} from '../../../../models/utils/platformCapabilities';
import { getSocialMediaPlatformLogoUrl } from '../../../../models/utils/socialUtils';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components-v2/Popover';
import { THEME } from '../../../components-v2/Theme/theme';
import { ChevronDown } from '../../svgs/icons/ChevronDown';
import { InfoIcon } from '../../svgs/icons/InfoIcon';
import { SimpleTooltip } from '../../Tooltip';
import { styleSheet } from './styles';

interface IProps {
	activePlatform?: Api.SocialMediaType;
	/** Undefined until GET social/platformCapabilities answers, and the panel stays hidden until it does. */
	capabilities?: Api.ISocialMediaPlatformCapabilitiesResponse;
	isSplit: boolean;
}

const GROUP_LABELS: Record<IPlatformCapabilityField['group'], string> = {
	content: 'Caption',
	media: 'Media',
};

const listPlatforms = (platforms: Api.SocialMediaType[]) => {
	if (platforms.length <= 1) {
		return platforms[0] ?? '';
	}
	return `${platforms.slice(0, -1).join(', ')} and ${platforms[platforms.length - 1]}`;
};

const describeNull = (field: IPlatformCapabilityField, platform: Api.SocialMediaType) =>
	field.nullMeaning === 'unavailable'
		? `Posts can’t carry this yet, so there is nothing to set for ${platform}.`
		: `We haven’t established this for ${platform}, so nothing is checked against it.`;

const describeReduction = (
	field: IPlatformCapabilityField,
	binding: Api.SocialMediaType[],
	combinedValue: Api.ISocialMediaPlatformCapabilities[IPlatformCapabilityField['key']]
) => {
	const names = listPlatforms(binding);
	const isPlural = binding.length > 1;
	if (field.reduction === 'all') {
		return combinedValue === true
			? `${field.reductionNote} Every targeted platform offers it.`
			: `${field.reductionNote} ${names} ${isPlural ? 'do' : 'does'} not.`;
	}
	if (field.reduction === 'any') {
		return combinedValue === true
			? `${field.reductionNote} ${names} ${isPlural ? 'require' : 'requires'} it.`
			: `${field.reductionNote} No targeted platform requires it.`;
	}
	return binding.length
		? `${field.reductionNote} ${names} ${isPlural ? 'set' : 'sets'} it.`
		: `${field.reductionNote} No targeted platform has established one.`;
};

const pluralize = (count: number, noun: string) => `${count.toLocaleString()} ${count === 1 ? noun : `${noun}s`}`;

// Splitting gives each version its own media but does not widen what can be added: the save path still
// holds every upload, whichever tab it was made on, to the tightest limits across the targeted platforms.
const describeSplitNote = (combined: Api.ISocialMediaCombinedPlatformCapabilities, platformCount: number) => {
	const versionNote = 'Each version is checked against its own platform’s limits.';
	if (platformCount <= 1) {
		return versionNote;
	}
	const items = pluralize(combined.maxMediaItems, 'item');
	const images = pluralize(combined.maxImages, 'image');
	const videos = pluralize(combined.maxVideos, 'video');
	const mixing = combined.allowsImagesWithVideo ? '' : ', and not images and video together';
	return `${versionNote} Uploads on every tab also count toward one post-wide limit, the tightest across all ${platformCount} platforms: at most ${items}, ${images} and ${videos}${mixing}. Crops don’t count.`;
};

// The strip has room for a glance, not for the table: the two ceilings people compose against go
// on the pill, and the rest waits behind it.
const describeSummary = (capability: Api.ISocialMediaPlatformCapabilities) =>
	`${capability.maxContentCharacters.toLocaleString()} chars · ${pluralize(capability.maxImages, 'image')}`;

export const PlatformLimits: React.FC<IProps> = ({ activePlatform, capabilities, isSplit }) => {
	const [open, setOpen] = React.useState(false);
	const platforms = capabilities?.platforms ?? [];
	// While split is on each version answers to its own platform, so the reduction across the set
	// describes nothing that gets published.
	const showCombined = !isSplit && platforms.length > 1;
	const summarySource = isSplit ? findPlatformCapabilities(capabilities, activePlatform) : capabilities?.combined;

	// There is nothing to say about limits we have not been told, and no local table to fall back on.
	if (!capabilities || !platforms.length || !summarySource) {
		return null;
	}

	const heading = isSplit
		? `${activePlatform} limits`
		: platforms.length > 1
			? `Limits across all ${platforms.length} platforms`
			: `${platforms[0].platform} limits`;
	// One word carries the pill; the sentence it stands for is still announced through the label. The
	// split strip has a tab naming the platform right beside it, so repeating the name there is spend
	// on width the controls need.
	const compactHeading = isSplit || platforms.length > 1 ? 'Limits' : heading;

	return (
		<Popover onOpenChange={setOpen} open={open}>
			<PopoverTrigger aria-label={heading} className={css(styleSheet.limitsTrigger)}>
				<InfoIcon fillColor={THEME.colors.icon.primary} />
				<span className={css(styleSheet.limitsTriggerLabel)}>{compactHeading}</span>
				<span className={css(styleSheet.limitsTriggerSummary)}>
					{describeSummary(summarySource as Api.ISocialMediaPlatformCapabilities)}
				</span>
				<ChevronDown className={css(styleSheet.limitsChevron, open && styleSheet.limitsChevronOpen)} />
			</PopoverTrigger>
			<PopoverContent align='start' styles={[styleSheet.limitsPopover]}>
				<p className={css(styleSheet.limitsPopoverHeading)}>{heading}</p>
				<div className={css(styleSheet.limitsScroll)}>
					<table className={css(styleSheet.limitsTable)}>
						<thead>
							<tr>
								<th className={css(styleSheet.limitsHeadCell, styleSheet.limitsHeadCellLabel)} scope='col'>
									Limit
								</th>
								{platforms.map(capability => (
									<th
										className={css(
											styleSheet.limitsHeadCell,
											isSplit && capability.platform === activePlatform && styleSheet.limitsActiveColumn
										)}
										key={capability.platform}
										scope='col'
									>
										<span className={css(styleSheet.limitsPlatformHead)}>
											<img
												alt=''
												className={css(styleSheet.tabLogo)}
												src={getSocialMediaPlatformLogoUrl(capability.platform)}
											/>
											{capability.platform}
										</span>
									</th>
								))}
								{showCombined ? (
									<th
										className={css(
											styleSheet.limitsHeadCell,
											styleSheet.limitsCombinedColumn,
											styleSheet.limitsCombinedTop
										)}
										scope='col'
									>
										This post
									</th>
								) : null}
							</tr>
						</thead>
						<tbody>
							{PLATFORM_CAPABILITY_FIELDS.map((field, index) => {
								const previousField = PLATFORM_CAPABILITY_FIELDS[index - 1];
								const isLastField = index === PLATFORM_CAPABILITY_FIELDS.length - 1;
								const combinedValue = capabilities.combined[field.key];
								const binding = getBindingPlatforms(field, platforms, capabilities.combined);
								return (
									<React.Fragment key={field.key}>
										{previousField?.group !== field.group ? (
											<tr>
												<th className={css(styleSheet.limitsGroupCell)} colSpan={platforms.length + 1} scope='colgroup'>
													{GROUP_LABELS[field.group]}
												</th>
												{showCombined ? <td className={css(styleSheet.limitsCombinedColumn)} /> : null}
											</tr>
										) : null}
										<tr className={css(styleSheet.limitsRow)}>
											<th className={css(styleSheet.limitsLabelCell)} scope='row'>
												{showCombined ? (
													<SimpleTooltip
														styles={[styleSheet.limitsTooltip]}
														title={describeReduction(field, binding, combinedValue)}
													>
														<span className={css(styleSheet.limitsLabel, styleSheet.limitsLabelHinted)}>
															{field.label}
														</span>
													</SimpleTooltip>
												) : (
													<span className={css(styleSheet.limitsLabel)}>{field.label}</span>
												)}
											</th>
											{platforms.map(capability => {
												const value = capability[field.key];
												const isUnknown = value === null || value === undefined;
												return (
													<td
														className={css(
															styleSheet.limitsValueCell,
															isUnknown && styleSheet.limitsValueUnknown,
															isSplit && capability.platform === activePlatform && styleSheet.limitsActiveColumn
														)}
														key={capability.platform}
													>
														{isUnknown ? (
															<SimpleTooltip
																styles={[styleSheet.limitsTooltip]}
																title={describeNull(field, capability.platform)}
															>
																<span>{formatCapabilityValue(field, value)}</span>
															</SimpleTooltip>
														) : (
															formatCapabilityValue(field, value)
														)}
													</td>
												);
											})}
											{showCombined ? (
												<td
													className={css(
														styleSheet.limitsValueCell,
														styleSheet.limitsCombinedColumn,
														styleSheet.limitsValueBinding,
														isLastField && styleSheet.limitsCombinedBottom
													)}
												>
													{formatCapabilityValue(field, combinedValue)}
												</td>
											) : null}
										</tr>
									</React.Fragment>
								);
							})}
						</tbody>
					</table>
				</div>
				<p className={css(styleSheet.limitsNote)}>
					{isSplit
						? describeSplitNote(capabilities.combined, platforms.length)
						: 'One post goes to every platform selected, so the strictest limit of each kind applies to all of them. Customize by platform to hold each version to its own limits.'}
				</p>
			</PopoverContent>
		</Popover>
	);
};
